import React, {ReactNode, memo, useCallback, useEffect, useMemo, useState} from "react"
import {Container} from "../../../components/Container"
import {Line} from "react-chartjs-2"
import {temperatureLineDatas, temperatureLineOptions} from "../Settings/settings/option"
import {useStaticData} from "../Settings/data/static"
import {SelectDevice} from "../Settings/components/SelectDevice"
import {SelectModel} from "../Settings/types/types"
import {useSelector} from "react-redux"
import {onClickHandler} from "../../../utils/onclick"
import {useLocalization} from "../../../hooks/localization"
import {useRPC} from "../../../hooks/rpc"
import {TempLogType} from "../../../store/control/state"
import {useOutsideDetector} from "../../../hooks/outside-detector"
import {SettingSelectComponent} from "../../../components/Settings/SettingSelectComponent"
import {SelectItem} from "../../../components/Select"
import "./index.scss"

const date = new Date()
export const initialYear = 2022
export const getDaysInMonth = (month: number, year: number): number => new Date(year, month, 0).getDate()
export const DateFilterPopup = memo(function DateFilterPopup({
    outsideClick,
    children
}: {
    outsideClick: React.MutableRefObject<any>
    children: ReactNode
}) {
    return (
        <div className="date-popup-overlay">
            <div className="date-popup-container" ref={outsideClick}>
                {children}
            </div>
        </div>
    )
})
const STATIC_TYPE = ["hourly_average_in_daily", "daily_average_in_montly", "montly_average_in_year"]
let timer
export const TemperatureStatistic = memo(function TemperatureStatistic() {
    const staticData = useStaticData()
    const [chartLabel, setChartLabel] = useState(STATIC_TYPE[0])
    const [dataLoader, setDataLoader] = useState(false)
    const [openPopup, setOpenPopup] = useState(false)
    const clickOutside = useOutsideDetector(openPopup, () => setOpenPopup(false))
    const [rpc] = useRPC()
    const {t} = useLocalization()
    const [statisticAverage, setStatisticAverage] = useState<TempLogType>({
        measured: [],
        set: []
    })
    const timeoutLoader = useCallback((status: boolean) => {
        setDataLoader(status)
        timer = setTimeout(() => {
            setDataLoader(!status)
        }, 3000)
        return () => clearTimeout(timer)
    }, [])

    //POPUP
    const [selectedFullDate, setSelectedFullDate] = useState<string>(
        `${date.getDate() >= 0 && date.getDate() < 9 ? "0" + date.getDate().toString() : date.getDate()}.${
            date.getMonth() + 1
        }.${date.getFullYear()}`
    )

    const [selectedDay, setSelectedDay] = useState<SelectItem<string>>(null)
    const [selectedMonth, setSelectedMonth] = useState<SelectItem<string>>(null)
    const [selectedYear, setSelectedYear] = useState<SelectItem<number>>(null)

    const filterDates = useCallback(() => {
        if (selectedDay == null || selectedMonth == null || selectedYear == null) {
            return
        }
        const newDate = `${selectedDay?.value}.${selectedMonth?.value}.${selectedYear.value}`
        setSelectedFullDate(newDate)
        setOpenPopup(false)
        timeoutLoader(true)
    }, [selectedDay, selectedMonth, selectedYear, timeoutLoader])

    const calculateYear: SelectItem<number>[] = useMemo(
        () =>
            Array.from({length: date.getFullYear() - initialYear + 1}, (_, index) => {
                return {label: (initialYear + index).toString(), value: initialYear + index}
            }) as [],
        []
    )

    const calculateMonth: SelectItem<string>[] = useMemo(
        () =>
            staticData.months.map((month, index) => {
                if (index >= 0 && index < 9) {
                    return {label: month, value: `0${(index + 1).toString()}`}
                }
                return {value: (index + 1).toString(), label: month}
            }),
        [staticData]
    )

    const renderDays: SelectItem<string>[] = useMemo(() => {
        if (selectedMonth?.value && selectedYear?.value) {
            const daysInSelectedMonth = getDaysInMonth(Number(selectedMonth?.value), selectedYear?.value)
            return Array.from({length: daysInSelectedMonth}, (_, index) => {
                if (index >= 0 && index < 9) {
                    return {label: (index + 1).toString(), value: `0${(index + 1).toString()}`}
                }
                return {label: (index + 1).toString(), value: (index + 1).toString()}
            })
        }
        return []
    }, [selectedMonth, selectedYear])
    //POPUP

    const setHours = useMemo(() => {
        const newV = []
        if (statisticAverage?.measured !== null) {
            for (const value of statisticAverage?.measured) {
                newV.push(value?.time_interval)
            }
        }
        return newV
    }, [statisticAverage])

    const setDays = useMemo(() => {
        const newV = []
        if (statisticAverage?.measured !== null) {
            for (const value of statisticAverage?.measured) {
                newV.push(Number(value?.time_interval.slice(0, 2)))
            }
        }
        return newV
    }, [statisticAverage])

    const setMonths = useMemo(() => {
        const newV = []
        if (statisticAverage?.measured !== null) {
            for (const value of statisticAverage?.measured) {
                newV.push(staticData?.months[Number(value?.time_interval.slice(0, 2)) - 1])
            }
        }
        return newV
    }, [statisticAverage, staticData])

    const chartLabels: string[] = useMemo(() => {
        if (chartLabel === STATIC_TYPE[0]) {
            return setHours
        } else if (chartLabel === STATIC_TYPE[1]) {
            return setDays
        } else if (chartLabel === STATIC_TYPE[2]) {
            return setMonths
        }
    }, [chartLabel, setDays, setHours, setMonths])

    const chartDatas1: number[] = useMemo(() => {
        const data = statisticAverage?.measured?.map(data => data?.average)
        if (chartLabel === STATIC_TYPE[0]) {
            return data
        } else if (chartLabel === STATIC_TYPE[1]) {
            return data
        } else if (chartLabel === STATIC_TYPE[2]) {
            return data
        }
    }, [chartLabel, statisticAverage?.measured])

    const chartDatas2: number[] = useMemo(() => {
        const data = statisticAverage?.set?.map(data => data?.mod)
        if (chartLabel === STATIC_TYPE[0]) {
            return data
        } else if (chartLabel === STATIC_TYPE[1]) {
            return data
        } else if (chartLabel === STATIC_TYPE[2]) {
            return data
        }
    }, [chartLabel, statisticAverage?.set])

    const onClickChangeType = useCallback(
        (type: number) => {
            let newLabel = chartLabel
            switch (type) {
                case 1:
                    newLabel = STATIC_TYPE[0]
                    timeoutLoader(true)
                    break
                case 2:
                    newLabel = STATIC_TYPE[1]
                    timeoutLoader(true)
                    break
                case 3:
                    newLabel = STATIC_TYPE[2]
                    timeoutLoader(true)
                    break
            }
            setChartLabel(newLabel)
        },
        [chartLabel, timeoutLoader]
    )

    const lineDatas = temperatureLineDatas(chartLabels, chartDatas1, chartDatas2)
    const controls = useSelector(state => state.control.controlList)
    const controlList = controls.filter(control => control?.temperature_settings)

    const Devices: SelectModel[] = useMemo(() => controlList, [controlList])
    const [selectedControl, setSelectedControl] = useState({
        id: Devices[0]?.id || null,
        selected: true
    })

    const changeDevice = useCallback(
        (id: string) => {
            Devices.find(device => device?.id === id && setSelectedControl({id: id, selected: true}))
            timeoutLoader(true)
        },
        [Devices, timeoutLoader]
    )

    useEffect(() => {
        rpc.getTemperatureLog(selectedControl?.id, chartLabel, selectedFullDate).then(data =>
            setStatisticAverage(
                data || {
                    set: [],
                    measured: []
                }
            )
        )
    }, [rpc, selectedControl, chartLabel, selectedFullDate])

    console.log(selectedControl?.id)

    useEffect(() => {
        //if (selectedControl?.id !== null) {
            if (statisticAverage?.set?.length > 0) {
                setDataLoader(false)
            } //else {
                //setDataLoader(true)
            //}
        //}
    }, [statisticAverage, /*selectedControl?.id*/])

    return (
        <>
            {openPopup && (
                <DateFilterPopup outsideClick={clickOutside}>
                    <div className="picker-field">
                        <span>{t("year")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={calculateYear}
                            onChange={setSelectedYear}
                            value={selectedYear}
                        />
                    </div>
                    <div className="picker-field">
                        <span>{t("month")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={calculateMonth}
                            onChange={setSelectedMonth}
                            value={selectedMonth}
                        />
                    </div>
                    <div className="picker-field">
                        <span>{t("day")}</span>
                        <SettingSelectComponent
                            label={""}
                            options={renderDays}
                            onChange={setSelectedDay}
                            value={selectedDay}
                        />
                    </div>
                    <div className="picker-field" style={{height: "100px"}}>
                        <button onClick={filterDates}>{t("select")}</button>
                    </div>
                </DateFilterPopup>
            )}
            <Container>
                <div className="statistic-bar-container">
                    <div className="statistic-bar-top-container">
                        <SelectDevice selectedDevice={selectedControl} changeDevice={changeDevice} />
                    </div>
                    <div className="statistic-bar">
                        {renderIf(!dataLoader)(
                            () => (
                                <>
                                    <div className="statatistic-bar-inner-top-container">
                                        <div className="date-filter-button" onClick={() => setOpenPopup(true)}>
                                            {t('pick_date')}
                                        </div>
                                        <span>
                                            <button
                                                {...onClickHandler(() => onClickChangeType(1))}
                                                className={`${
                                                    chartLabel === "hourly_average_in_daily"
                                                        ? "device-active-button"
                                                        : ""
                                                }`}
                                            >
                                                {t("energy_page.hour")}
                                            </button>
                                            <button
                                                {...onClickHandler(() => onClickChangeType(2))}
                                                className={`${
                                                    chartLabel === "daily_average_in_montly"
                                                        ? "device-active-button"
                                                        : ""
                                                }`}
                                            >
                                                {t("energy_page.day")}
                                            </button>
                                            <button
                                                {...onClickHandler(() => onClickChangeType(3))}
                                                className={`${
                                                    chartLabel === "montly_average_in_year"
                                                        ? "device-active-button"
                                                        : ""
                                                }`}
                                            >
                                                {t("energy_page.month")}
                                            </button>
                                        </span>
                                    </div>
                                    <span>
                                        {/* @ts-ignore */}
                                        <Line data={lineDatas} options={temperatureLineOptions} />
                                    </span>
                                </>
                            ),
                            () => (
                                <div className="chart-loader">
                                    <span />
                                </div>
                            )
                        )}
                    </div>
                </div>
            </Container>
        </>
    )
})
